import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { useContext, useEffect, useState } from "react";
import { getFileFromS3 } from "services/schedule-versions-services";
import { compareStringsAlphabetically } from "utils/utils";
import type { FilterOptions } from "components/schedule-hierarchy/schedule-filter-bar";
import {
  LINK_RECOMMENDER_JOB_LATEST_QUERY,
  LINK_RECOMMENDER_JOB_QUERY
} from "../queries/queries";

export const RECOMMENDER_JOB_POLL_INTERVAL_MS = 5000;

export type ActivityFilterMetadata = {
  phase: string;
  csi_division: string;
  activity_type: string;
  location_tags: string;
};

export type TLinkRecommenderJobOutputs = {
  matches?: string;
  predictions?: string;
  activity_filters?: string;
  recommendations_inserted?: string;
};

export type TLinkRecommenderJob = {
  id: string;
  status: string;
  created_at: string;
  outputs: TLinkRecommenderJobOutputs | null;
};

const PENDING_JOB_STATUSES = new Set(["queue", "queqe", "queued", "running"]);

export type LinkRecommenderJobUiState = {
  hasCompletedRecommenderJob: boolean;
  isRecommenderJobPending: boolean;
  hasRecommenderJobError: boolean;
};

export function getLinkRecommenderJobUiState(
  jobs: TLinkRecommenderJob[] | undefined
): LinkRecommenderJobUiState {
  const status = (jobs?.[0]?.status ?? "").trim().toLowerCase();
  return {
    hasCompletedRecommenderJob: status === "done",
    isRecommenderJobPending: PENDING_JOB_STATUSES.has(status),
    hasRecommenderJobError: status === "error"
  };
}

const EMPTY_MAP = new Map<string, ActivityFilterMetadata>();

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const isS3ErrorResponse = (json: unknown): boolean => {
  const rec = asRecord(json);
  return Boolean(rec && "error" in rec && Object.keys(rec).length === 1);
};

const pickActivityId = (row: Record<string, unknown>): string | undefined => {
  const id =
    row.feature_instance_id ?? row.activity_feature_instance_id ?? row.id;
  return typeof id === "string" && id.trim() ? id : undefined;
};

const toMetadataEntry = (
  row: Record<string, unknown>
): ActivityFilterMetadata => ({
  phase: String(row.phase ?? ""),
  csi_division: String(row.csi_division ?? row.csi_div ?? ""),
  activity_type: String(row.activity_type ?? ""),
  location_tags: String(row.location_tags ?? row.location ?? "")
});

const addRowToMap = (
  map: Map<string, ActivityFilterMetadata>,
  row: unknown
): void => {
  const rec = asRecord(row);
  if (!rec) return;
  const entry = toMetadataEntry(rec);
  const id = pickActivityId(rec);
  if (id) {
    map.set(id, entry);
  }
  const activityCode = rec.activity_id;
  if (typeof activityCode === "string" && activityCode.trim()) {
    map.set(activityCode.trim(), entry);
  }
};

export function parseActivityFiltersJson(
  json: unknown
): Map<string, ActivityFilterMetadata> {
  const map = new Map<string, ActivityFilterMetadata>();
  if (!json || isS3ErrorResponse(json)) return map;

  if (Array.isArray(json)) {
    json.forEach((row) => addRowToMap(map, row));
    return map;
  }

  const obj = asRecord(json);
  if (!obj) return map;

  const nestedRows = obj.activities ?? obj.data ?? obj.rows;
  if (Array.isArray(nestedRows)) {
    nestedRows.forEach((row) => addRowToMap(map, row));
    return map;
  }

  Object.entries(obj).forEach(([key, value]) => {
    const rec = asRecord(value);
    if (!rec) return;
    const id = pickActivityId(rec) || key;
    map.set(id, toMetadataEntry(rec));
    const activityCode = rec.activity_id;
    if (typeof activityCode === "string" && activityCode.trim()) {
      map.set(activityCode.trim(), toMetadataEntry(rec));
    }
  });

  return map;
}

export function buildFilterOptionsFromMetadata(
  metadataMap: Map<string, ActivityFilterMetadata>
): FilterOptions {
  const phases = new Set<string>();
  const activityTypes = new Set<string>();
  const csiDivisions = new Set<string>();
  const locations = new Set<string>();

  for (const entry of metadataMap.values()) {
    if (entry.phase && entry.phase !== "Root") phases.add(entry.phase);
    if (entry.activity_type && entry.activity_type !== "Summary") {
      activityTypes.add(entry.activity_type);
    }
    if (entry.csi_division) csiDivisions.add(entry.csi_division);
    if (entry.location_tags) locations.add(entry.location_tags);
  }

  return {
    phases: [...phases].sort(compareStringsAlphabetically),
    activityTypes: [...activityTypes].sort(compareStringsAlphabetically),
    csiDivisions: [...csiDivisions].sort(compareStringsAlphabetically),
    locations: [...locations].sort(compareStringsAlphabetically)
  };
}

export default function useActivityFiltersMetadata(
  enabled = true,
  xerS3Key?: string
) {
  const { gqlClientForProject } = useContext(ProjectContext);
  const [metadataMap, setMetadataMap] =
    useState<Map<string, ActivityFilterMetadata>>(EMPTY_MAP);
  const [loading, setLoading] = useState(false);
  const trimmedXerS3Key = xerS3Key?.trim() || undefined;
  const shouldFetch = Boolean(enabled && gqlClientForProject);
  const jobQuery = trimmedXerS3Key
    ? LINK_RECOMMENDER_JOB_QUERY
    : LINK_RECOMMENDER_JOB_LATEST_QUERY;

  const {
    data,
    loading: queryLoading,
    startPolling,
    stopPolling
  } = useCIQQuery<{
    link_recommender_job: TLinkRecommenderJob[];
  }>(jobQuery, {
    client: gqlClientForProject,
    skip: !shouldFetch,
    variables: trimmedXerS3Key ? { xer_s3_key: trimmedXerS3Key } : undefined
  });

  const {
    hasCompletedRecommenderJob,
    isRecommenderJobPending,
    hasRecommenderJobError
  } = getLinkRecommenderJobUiState(data?.link_recommender_job);

  useEffect(() => {
    if (!shouldFetch || !isRecommenderJobPending) {
      stopPolling?.();
      return undefined;
    }

    startPolling?.(RECOMMENDER_JOB_POLL_INTERVAL_MS);
    return () => {
      stopPolling?.();
    };
  }, [isRecommenderJobPending, shouldFetch, startPolling, stopPolling]);

  const activityFiltersPath =
    shouldFetch && hasCompletedRecommenderJob
      ? data?.link_recommender_job?.[0]?.outputs?.activity_filters
      : undefined;

  useEffect(() => {
    if (!shouldFetch) {
      setMetadataMap(EMPTY_MAP);
      setLoading(false);
      return undefined;
    }

    if (!activityFiltersPath) {
      setMetadataMap(EMPTY_MAP);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    getFileFromS3(activityFiltersPath)
      .then((json) => {
        if (cancelled) return;
        setMetadataMap(parseActivityFiltersJson(json));
      })
      .catch(() => {
        if (cancelled) return;
        setMetadataMap(EMPTY_MAP);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activityFiltersPath, shouldFetch]);

  return {
    metadataMap,
    loading: shouldFetch && (queryLoading || loading),
    hasCompletedRecommenderJob,
    isRecommenderJobPending,
    hasRecommenderJobError,
    recommenderJobId: data?.link_recommender_job?.[0]?.id
  };
}
